import { useState, useCallback, useEffect, useRef } from 'react';
import {
  RotateCcw,
  CheckCircle2,
  FolderOpen,
  Terminal,
  GitBranch,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Terminal as TerminalComponent } from '@/components/terminal/Terminal';
import { GitVisualization } from '@/components/visualization/GitVisualization';
import { CommandHint, CommandStatus } from '@/components/lesson/CommandHint';
import { LockedStepItem } from '@/components/lesson/LockedSection';
import { FileExplorer } from '@/components/lesson/FileExplorer';
import { FileViewer } from '@/components/lesson/FileViewer';
import { cn } from '@/lib/utils';
import { validateCommand } from '@/lib/git/commandValidator';
import { useGitStore } from '@/store/gitStore';
import { useLessonStore } from '@/store/lessonStore';
import { useTerminalStore } from '@/store/terminalStore';
import { gitEngine } from '@/lib/git/engine';
import { getPresetForLesson } from '@/lib/git/presets';
import type { LessonStep, HintLevel } from '@/types/lesson';
import type { GitPreset } from '@/lib/git/presets/types';
import type { RepoFile } from '@/types/git';

// Ключи для localStorage
const WORKSPACE_STATE_KEY = 'git-course-workspace-state';

interface WorkspaceState {
  showVisualization: boolean;
  showFiles: boolean;
  terminalCollapsed: boolean;
  vizCollapsed: boolean;
}

interface InteractiveWorkspaceProps {
  steps: LessonStep[];
  lessonId: string;
  onStepComplete?: (stepId: number) => void;
  onLessonComplete?: () => void;
}

/**
 * Интерактивный блок с терминалом и визуализацией
 * Оптимизированная версия с компактными компонентами и collapsible секциями
 */
export function InteractiveWorkspace({
  steps,
  lessonId,
  onStepComplete,
  onLessonComplete,
}: InteractiveWorkspaceProps) {
  // Загружаем сохранённое состояние
  const [workspaceState, setWorkspaceState] = useState<WorkspaceState>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(WORKSPACE_STATE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved) as WorkspaceState;
        } catch {
          // ignore
        }
      }
    }
    return {
      showVisualization: true,
      showFiles: false,
      terminalCollapsed: false,
      vizCollapsed: false,
    };
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [hintLevel, setHintLevel] = useState<HintLevel>(1);
  const [selectedFile, setSelectedFile] = useState<RepoFile | null>(null);
  const [commandStatus, setCommandStatus] = useState<'idle' | 'success' | 'error' | 'hint'>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');

  const { reset: resetRepo, loadPreset, clearPersistedState } = useGitStore();
  const {
    startLesson,
    completeStep,
    completeLesson,
    useHint: markHintUsed,
    recordAttempt,
  } = useLessonStore();
  const { clearHistory } = useTerminalStore();

  // Ref для хранения текущего preset (для сброса)
  const currentPresetRef = useRef<GitPreset | null>(null);

  const activeStep = steps[currentStep];
  const isLessonComplete = completedSteps.size === steps.length;

  // Сохраняем состояние при изменении
  useEffect(() => {
    localStorage.setItem(WORKSPACE_STATE_KEY, JSON.stringify(workspaceState));
  }, [workspaceState]);

  // Инициализация и сброс при смене урока
  useEffect(() => {
    console.log('[InteractiveWorkspace] Lesson changed to:', lessonId);

    // Сбрасываем состояние при смене урока
    setCurrentStep(0);
    setCompletedSteps(new Set());
    setHintLevel(1);
    setCommandStatus('idle');
    setStatusMessage('');

    // Очищаем историю терминала
    clearHistory();

    // Получаем preset для урока
    const preset = getPresetForLesson(lessonId);
    currentPresetRef.current = preset ?? null;

    if (preset) {
      // Загружаем preset в gitStore
      console.log('[InteractiveWorkspace] Loading preset:', preset.id);
      // Сначала очищаем persisted state, затем загружаем preset
      clearPersistedState().then(() => {
        loadPreset(preset);
        // Также синхронизируем с gitEngine для обратной совместимости
        gitEngine.loadPreset(preset);
      });
    } else {
      // Если preset нет - инициализируем пустой репозиторий
      console.log('[InteractiveWorkspace] No preset found, initializing empty repo');
      clearPersistedState().then(() => {
        gitEngine.reset();
        gitEngine.initializeRepo();
        resetRepo();
      });
    }

    // Инициализируем урок
    startLesson(lessonId);
  }, [lessonId, startLesson, resetRepo, clearHistory, loadPreset, clearPersistedState]);

  // Обработка команды из терминала
  const handleCommand = useCallback(
    (command: string): { success: boolean; message: string } => {
      if (!activeStep) {
        return { success: false, message: 'No active step' };
      }

      // Записываем попытку
      recordAttempt(lessonId, activeStep.id);

      // Валидируем команду
      const result = validateCommand(
        command,
        activeStep.expectedCommand,
        activeStep.validationRegex
      );

      if (result.isValid) {
        // Команда верна
        setCompletedSteps((prev) => new Set([...prev, activeStep.id]));
        setHintLevel(1);
        setCommandStatus('success');
        setStatusMessage(result.feedback ?? 'Correct! Well done!');

        // Отмечаем шаг как завершённый
        completeStep(lessonId, activeStep.id);

        // Коллбэк для родительского компонента
        onStepComplete?.(activeStep.id);

        // Переходим к следующему шагу
        if (currentStep < steps.length - 1) {
          setTimeout(() => {
            setCurrentStep(currentStep + 1);
            setCommandStatus('idle');
          }, 1500);
        } else {
          // Урок завершён - отмечаем в store
          completeLesson(lessonId);
          onLessonComplete?.();
        }

        // Показываем визуализацию после git init
        if (command.trim().toLowerCase() === 'git init') {
          setWorkspaceState((prev) => ({ ...prev, showVisualization: true }));
        }

        return { success: true, message: result.feedback ?? 'Correct!' };
      }

      // Команда неверна
      setCommandStatus('error');
      setStatusMessage(result.feedback ?? 'Not quite right. Try again!');

      return { success: false, message: result.feedback ?? 'Not quite right. Try again!' };
    },
    [
      activeStep,
      currentStep,
      steps.length,
      lessonId,
      recordAttempt,
      completeStep,
      completeLesson,
      onStepComplete,
      onLessonComplete,
    ]
  );

  // Сброс урока к исходному состоянию
  const handleReset = useCallback(() => {
    setCurrentStep(0);
    setCompletedSteps(new Set());
    setHintLevel(1);
    setCommandStatus('idle');
    setStatusMessage('');

    // Сбрасываем к preset или к пустому репозиторию
    if (currentPresetRef.current) {
      console.log('[InteractiveWorkspace] Resetting to preset:', currentPresetRef.current.id);
      loadPreset(currentPresetRef.current);
      gitEngine.loadPreset(currentPresetRef.current);
    } else {
      console.log('[InteractiveWorkspace] Resetting to empty repo');
      resetRepo();
      gitEngine.reset();
      gitEngine.initializeRepo();
    }
  }, [resetRepo, loadPreset]);

  // Показать следующую подсказку
  const handleShowNextHint = useCallback(() => {
    if (hintLevel < 3) {
      const newLevel = (hintLevel + 1) as HintLevel;
      setHintLevel(newLevel);
      markHintUsed(lessonId, activeStep?.id ?? 0);
      setCommandStatus('hint');
      setStatusMessage(`Подсказка уровня ${newLevel}`);
    }
  }, [hintLevel, lessonId, activeStep, markHintUsed]);

  // Использовать решение (автоматически ввести команду)
  const handleUseSolution = useCallback(() => {
    if (activeStep) {
      // Симулируем ввод команды
      handleCommand(activeStep.expectedCommand);
    }
  }, [activeStep, handleCommand]);

  // Переключение секции
  const toggleSection = useCallback((section: 'visualization' | 'files' | 'terminal') => {
    setWorkspaceState((prev) => {
      switch (section) {
        case 'visualization':
          return { ...prev, showVisualization: !prev.showVisualization };
        case 'files':
          return { ...prev, showFiles: !prev.showFiles };
        case 'terminal':
          return { ...prev, terminalCollapsed: !prev.terminalCollapsed };
        default:
          return prev;
      }
    });
  }, []);

  return (
    <div className="interactive-workspace flex h-full flex-col text-sm">
      {/* Header - компактный */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 bg-slate-900/30">
        <div className="flex items-center gap-3">
          <h3 className="text-xs font-medium text-slate-300">Interactive Practice</h3>
          {/* Step indicator - миниатюрный */}
          <div className="flex items-center gap-1">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  completedSteps.has(step.id)
                    ? 'bg-green-400'
                    : index === currentStep
                      ? 'bg-git-orange'
                      : 'bg-slate-600'
                )}
                title={step.title}
              />
            ))}
          </div>
          <span className="text-[10px] text-slate-500">
            {completedSteps.size}/{steps.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleSection('files')}
            className={cn(
              'h-6 px-2 text-xs',
              workspaceState.showFiles ? 'text-blue-400' : 'text-slate-500'
            )}
          >
            <FolderOpen className="mr-1 h-3 w-3" />
            Files
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleSection('visualization')}
            className={cn(
              'h-6 px-2 text-xs',
              workspaceState.showVisualization ? 'text-git-orange' : 'text-slate-500'
            )}
          >
            <GitBranch className="mr-1 h-3 w-3" />
            Viz
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="h-6 px-2 text-xs text-slate-500"
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Reset
          </Button>
        </div>
      </div>

      {/* Steps sidebar - компактный */}
      <div className="border-b border-slate-700/30 bg-slate-900/20 px-3 py-1.5 overflow-x-auto">
        <div className="flex gap-1">
          {steps.map((step, index) => (
            <LockedStepItem
              key={step.id}
              stepNumber={index + 1}
              title={step.title}
              isLocked={index > currentStep && !completedSteps.has(step.id)}
              isCompleted={completedSteps.has(step.id)}
              isCurrent={index === currentStep && !completedSteps.has(step.id)}
              onClick={() => {
                if (index <= currentStep || completedSteps.has(step.id)) {
                  setCurrentStep(index);
                  setHintLevel(1);
                  setCommandStatus('idle');
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* Current step - компактный */}
      {activeStep && !isLessonComplete && (
        <div className="border-b border-slate-700/30 bg-slate-900/20 px-3 py-2">
          <div className="flex items-start gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-git-orange text-[10px] font-medium text-white flex-shrink-0">
              {activeStep.id}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-200">{activeStep.title}</span>
                {commandStatus !== 'idle' && (
                  <CommandStatus status={commandStatus} message={statusMessage} compact />
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">{activeStep.description}</p>
              {/* Компактная подсказка */}
              <div className="mt-1.5">
                <CommandHint
                  hint={activeStep.hint}
                  currentLevel={hintLevel}
                  onShowNextHint={handleShowNextHint}
                  onUseSolution={handleUseSolution}
                  compact
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lesson complete */}
      {isLessonComplete && (
        <div className="border-b border-green-700/30 bg-green-900/10 px-3 py-2">
          <div className="flex items-center gap-2 text-green-400 text-xs">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-medium">Lesson Complete!</span>
          </div>
        </div>
      )}

      {/* Content - Terminal, Visualization and Files */}
      <div className="flex flex-1 min-h-0">
        {/* File Explorer sidebar */}
        {workspaceState.showFiles && (
          <div className="w-56 flex-shrink-0 border-r border-slate-700/30 bg-slate-900/20">
            <FileExplorer
              selectedPath={selectedFile?.path}
              onFileSelect={(file) => setSelectedFile(file)}
            />
          </div>
        )}

        {/* Main content area */}
        <div className="flex flex-1 flex-col min-h-0">
          {/* Terminal - collapsible */}
          <div
            className={cn(
              'transition-all duration-200',
              workspaceState.terminalCollapsed ? 'h-8' : 'flex-1 min-h-[180px]'
            )}
          >
            {/* Terminal header */}
            <button
              onClick={() => toggleSection('terminal')}
              className="w-full flex items-center gap-2 px-2 py-1 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
            >
              <Terminal className="h-3 w-3 text-slate-400" />
              <span className="text-[10px] text-slate-400">Terminal</span>
              {workspaceState.terminalCollapsed ? (
                <ChevronDown className="h-3 w-3 text-slate-500 ml-auto" />
              ) : (
                <ChevronUp className="h-3 w-3 text-slate-500 ml-auto" />
              )}
            </button>

            {/* Terminal content */}
            {!workspaceState.terminalCollapsed && (
              <TerminalComponent
                onCommand={handleCommand}
                expectedCommand={activeStep?.expectedCommand}
                resetKey={lessonId}
                onCorrectCommand={(cmd) => {
                  console.log('Correct command entered:', cmd);
                }}
              />
            )}
          </div>

          {/* Visualization - collapsible */}
          {workspaceState.showVisualization && (
            <div
              className={cn(
                'border-t border-slate-700/30 transition-all duration-200',
                workspaceState.vizCollapsed ? 'h-8' : 'h-1/2 min-h-[180px]'
              )}
            >
              {/* Visualization header */}
              <button
                onClick={() =>
                  setWorkspaceState((prev) => ({ ...prev, vizCollapsed: !prev.vizCollapsed }))
                }
                className="w-full flex items-center gap-2 px-2 py-1 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <GitBranch className="h-3 w-3 text-slate-400" />
                <span className="text-[10px] text-slate-400">Visualization</span>
                {workspaceState.vizCollapsed ? (
                  <ChevronDown className="h-3 w-3 text-slate-500 ml-auto" />
                ) : (
                  <ChevronUp className="h-3 w-3 text-slate-500 ml-auto" />
                )}
              </button>

              {/* Visualization content */}
              {!workspaceState.vizCollapsed && (
                <div className="h-[calc(100%-24px)] flex">
                  <div className="flex-1">
                    <GitVisualization />
                  </div>
                  {selectedFile && (
                    <div className="w-1/2 border-l border-slate-700/30">
                      <FileViewer file={selectedFile} onClose={() => setSelectedFile(null)} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
